import { Box, Typography } from '@mui/material'

import DeskListItem from '../../components/desk/DeskListItem'
import { ListDesksProps } from '../../types/ComponentProps'
import Database from '../../types/Database'
import { formatDateToString } from '../../utils/FormatHelpers'

export default function ListDesks<T extends Database.Desk = Database.Desk>({
  desks,
  onDeskClicked,
  onDeskDeleted,
  onDeskEdit,
  selectedDesks,
  readOnly = false,
  title,
  bookings,
  dateRange
}: ListDesksProps<T>) {
  const isDeskSelected = (desk: T): boolean => {
    return !!(
      selectedDesks &&
      selectedDesks.length > 0 &&
      selectedDesks.find(x => x.office === desk.office && x.name === desk.name)
    )
  }

  const getBookingsForDesk = (desk: T): Database.Booking[] => {
    if (!bookings) return []

    let filteredBookings = bookings.filter(
      booking => booking.did === desk.did && booking.oid === desk.office
    )

    // Filter by date range if provided
    if (dateRange && dateRange.length > 0) {
      const dateStrings = dateRange.map(d => formatDateToString(d))
      filteredBookings = filteredBookings.filter(booking => {
        if (!booking.forDate) return false
        const bookingDateStr = formatDateToString(booking.forDate)
        return dateStrings.includes(bookingDateStr)
      })
    }

    return filteredBookings
  }

  const renderDeskItems = () => {
    if (!desks) return []

    return desks.map(desk => (
      <DeskListItem<T>
        key={desk.id}
        desk={desk}
        isSelected={isDeskSelected(desk)}
        readOnly={readOnly}
        onDeskClick={onDeskClicked}
        onDeskDelete={onDeskDeleted || undefined}
        onDeskEdit={onDeskEdit || undefined}
        bookingsForDesk={getBookingsForDesk(desk)}
      />
    ))
  }

  return (
    <Box>
      {title && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography component='h1' variant='caption'>
            {title}
          </Typography>
        </Box>
      )}
      <Box
        data-testid='desklist'
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)'
          },
          gap: 1,
          p: 1
        }}
      >
        {renderDeskItems()}
      </Box>
    </Box>
  )
}
